<script lang="ts">
	import MapPinIcon from "@lucide/svelte/icons/map-pin";
	import Share2Icon from "@lucide/svelte/icons/share-2";
	import MailIcon from "@lucide/svelte/icons/mail";
	import PhoneCallIcon from "@lucide/svelte/icons/phone-call";
	import { Button } from "#lib/web-ui/ui/button/index.js";
	import * as Drawer from "#lib/web-ui/ui/drawer/index.js";
	import { formatCurrency, formatCurrencyRound } from "@fergmar/ui/formatters";
	import { MortgageCalculator } from "#lib/web-ui/calculators/mortgage-calculator/index.js";
	import { cloudinaryUrl, cloudinaryFetch } from "#lib/shared/cloudinary.js";
	import type { PublicInvestmentListing } from "@fergmar/contracts/public/investment-listings";
	import SalesSlugContactForm from "./sales-slug-contact-form.svelte";
	import { ListingImageGallery, MatterportTour } from "#lib/web-ui/primitives/index.js";
	import { copyToClipboard } from "@fergmar/ui/clipboard";
	import { buildShortUrl } from "@fergmar/contracts/public/short-links";
	import { serializeScriptValue } from "@fergmar/ui/script";

	type PublicListing = PublicInvestmentListing;

	let {
		listing,
		salesTeam
	}: {
		listing: PublicListing;
		salesTeam: {
			displayName: string;
			title: string;
			headshot: string;
			workEmail: string;
			workNumber: string | null;
		}[];
	} = $props();

	let applyDrawerOpen = $state(false);
	let shortUrlCopied = $state(false);
	let copiedTimer: ReturnType<typeof setTimeout> | undefined;

	$effect(() => () => clearTimeout(copiedTimer));

	async function copyShortUrl() {
		if (await copyToClipboard(buildShortUrl(listing.addressStreet1))) {
			shortUrlCopied = true;
			clearTimeout(copiedTimer);
			copiedTimer = setTimeout(() => {
				shortUrlCopied = false;
			}, 2000);
		}
	}

	const galleryMedia = $derived(
		listing?.media?.map((m) => ({
			url: m.galleryUrl ?? m.publicUrl,
			lightboxUrl: m.publicUrl
		})) ?? []
	);
	const primaryGalleryUrl = $derived(
		listing?.media?.[0]?.galleryUrl ?? listing?.media?.[0]?.publicUrl
	);

	const featuredImage = $derived(
		cloudinaryFetch(primaryGalleryUrl, { width: 1200 }) ||
			cloudinaryUrl("brand/fergmar/fergmar-logo-dark", { width: 300 })
	);
	const listingUpdatedAt = $derived(new Date(listing.updatedAt));
	const listingValidThrough = $derived(
		new Date(listingUpdatedAt.getTime() + 365 * 24 * 60 * 60 * 1000).toISOString()
	);

	const propertySchema = $derived({
		"@context": "https://schema.org",
		"@type": "RealEstateListing",
		name: listing.addressStreet1 || "",
		description: listing.description || "",
		url: `https://fergmar.com/properties/for-sale/${listing.slug}`,
		price: listing.currentPrice || 0,
		priceCurrency: "USD",
		image: featuredImage,
		datePosted: listingUpdatedAt.toISOString(),
		validThrough: listingValidThrough,
		address: {
			"@type": "PostalAddress",
			streetAddress: `${listing.addressStreet1 || ""} ${listing.addressStreet2 || ""}`.trim(),
			addressLocality: listing.addressCity || "",
			addressRegion: listing.addressState || "",
			postalCode: listing.addressZip || "",
			addressCountry: "US"
		},
		numberOfRooms: listing.bedrooms || 0,
		numberOfBathroomsFull: listing.fullBathrooms || 0,
		numberOfBathroomsPartial: listing.halfBathrooms || 0,
		floorSize: {
			"@type": "QuantitativeValue",
			value: listing.livingArea || 0,
			unitCode: "FTK"
		},
		offers: {
			"@type": "Offer",
			price: listing.currentPrice || 0,
			priceCurrency: "USD",
			availability: "https://schema.org/InStock",
			validFrom: listingUpdatedAt.toISOString(),
			seller: {
				"@type": "Organization",
				name: "Fergmar",
				description: "Property Owner"
			}
		},
		seller: {
			"@type": "Organization",
			name: "Fergmar",
			telephone: "(361) 200-1175",
			email: "invest@fergmar.com",
			url: "https://fergmar.com",
			address: {
				"@type": "PostalAddress",
				streetAddress: "4800 N Navarro, Suite 100",
				addressLocality: "Victoria",
				addressRegion: "TX",
				postalCode: "77904",
				addressCountry: "US"
			},
			areaServed: {
				"@type": "City",
				name: "Victoria",
				addressRegion: "TX"
			}
		}
	});
</script>

<svelte:head>
	<title
		>{listing.addressStreet1}, {listing.addressStreet2 ? `${listing.addressStreet2}, ` : ""}
		{listing.addressCity}, TX {listing.addressZip}
		| Owner Direct Sale | Fergmar</title
	>

	<meta
		name="description"
		content="{listing.addressStreet1} {listing.addressStreet2} in {listing.addressCity}, TX for sale by owner. {listing.bedrooms} bed, {listing.fullBathrooms} bath, {listing.livingArea} sqft home priced at {formatCurrency(
			listing.currentPrice
		)} by Victoria TX property owner Fergmar."
	/>

	<meta
		property="og:title"
		content="{listing.addressStreet1}, {listing.addressStreet2}, {listing.addressCity}, TX {listing.addressZip} | Owner Direct Sale"
	/>
	<meta
		property="og:description"
		content="{listing.bedrooms} bed, {listing.fullBathrooms} bath home for sale by owner in {listing.addressCity}, TX. Direct from Victoria property owner at {formatCurrency(
			listing.currentPrice
		)}."
	/>
	<meta property="og:type" content="place" />
	<meta property="og:url" content="https://fergmar.com/properties/for-sale/{listing.slug}" />
	{#if primaryGalleryUrl}
		<meta
			property="og:image"
			content={cloudinaryFetch(primaryGalleryUrl, {
				width: 1200,
				height: 630,
				crop: "fill"
			})}
		/>
	{/if}
	<meta property="og:site_name" content="Fergmar" />
	<meta property="og:locality" content={listing.addressCity} />
	<meta property="og:region" content="TX" />
	<meta property="og:postal-code" content={listing.addressZip} />
	<meta property="og:street-address" content={listing.addressStreet1} />
	<meta property="og:country-name" content="USA" />

	<meta name="twitter:card" content="summary_large_image" />
	<meta
		name="twitter:title"
		content="{listing.addressStreet1}, {listing.addressStreet2}, {listing.addressCity}, TX - Owner Direct"
	/>
	<meta
		name="twitter:description"
		content="{listing.bedrooms} bed, {listing.fullBathrooms} bath home for sale by owner in {listing.addressCity}, TX"
	/>
	{#if primaryGalleryUrl}
		<meta
			name="twitter:image"
			content={cloudinaryFetch(primaryGalleryUrl, {
				width: 1200,
				height: 630,
				crop: "fill"
			})}
		/>
	{/if}

	<svelte:element this={"script"} type="application/ld+json">
		{serializeScriptValue(propertySchema)}
	</svelte:element>
</svelte:head>

<div class="pb-24">
	{#key listing.slug}
		<ListingImageGallery media={galleryMedia} />
	{/key}

	<div class="mx-auto max-w-[1400px] px-6 pt-6 lg:px-8">
		<div class="grid grid-cols-1 gap-8 pt-4 lg:grid-cols-3">
			<div class="lg:col-span-2">
				<div id="property-header" class="mb-6">
					<div class="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
						<div class="order-2 md:order-0">
							<div class="mb-2 flex flex-wrap gap-2">
								{#if listing.status}
									<span
										class="rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-600 uppercase"
									>
										{listing.status}
									</span>
								{/if}
							</div>
							<div class="flex items-start gap-3">
								<MapPinIcon class="text-primary/70 mt-1 h-6 w-6" />
								<div class="min-w-0">
									<h2 class="text-2xl font-bold">
										{listing.addressStreet1 || "Property Details"}
									</h2>

									<h3 class="text-muted-foreground text-lg">
										{listing.addressStreet2
											? `${listing.addressStreet2}, `
											: ""}
										{listing.addressCity}, {listing.addressState}
										{listing.addressZip}
									</h3>
								</div>
							</div>
							<button
								type="button"
								class="text-primary hover:text-primary/80 mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded transition-colors"
								aria-label={shortUrlCopied ? "Short URL copied" : "Copy short URL"}
								title={shortUrlCopied ? "Copied!" : "Copy short URL"}
								onclick={copyShortUrl}
							>
								<Share2Icon class="h-5 w-5" />
							</button>
							{#if shortUrlCopied}
								<span class="text-primary mt-1 text-sm" role="status">Copied!</span>
							{/if}
						</div>
						<div
							class="border-border/70 text-muted-foreground order-1 mt-auto rounded border border-b bg-gray-100/70 p-4 text-center md:order-0 md:border-b-0 md:pr-6 md:pl-6 md:text-right"
						>
							<span>Asking Price</span>
							<div class="text-4xl font-bold">
								{#if listing.currentPrice}
									{formatCurrencyRound(listing.currentPrice)}
								{:else}
									Contact for Price
								{/if}
							</div>
						</div>
					</div>
				</div>

				<div
					class="border-border bg-card mb-8 grid grid-cols-2 gap-4 rounded border p-6 sm:grid-cols-4"
				>
					<div class="text-center">
						<div class="text-2xl font-bold">
							{listing.bedrooms || "N/A"}
						</div>
						<div class="text-muted-foreground text-sm">Beds</div>
					</div>
					<div class="text-center">
						<div class="text-2xl font-bold">
							{listing.fullBathrooms || 0}
						</div>
						<div class="text-muted-foreground text-sm">Full Baths</div>
					</div>
					<div class="text-center">
						<div class="text-2xl font-bold">
							{listing.halfBathrooms || 0}
						</div>
						<div class="text-muted-foreground text-sm">Half Baths</div>
					</div>
					<div class="text-center">
						{#if listing.propertyType === "Land" || listing.propertyType === "Farm & Ranch"}
							<div class="text-2xl font-bold">
								{listing.livingArea || "N/A"}
							</div>
							<div class="text-muted-foreground text-sm">Acres</div>
						{:else}
							<div class="text-2xl font-bold">
								{listing.livingArea || "N/A"}
							</div>
							<div class="text-muted-foreground text-sm">Square Feet</div>
						{/if}
					</div>
				</div>

				<div id="description" class="mb-8">
					<h2 class="mb-4 text-2xl font-bold">About the Property</h2>
					<p class="text-muted-foreground">
						{listing.description || "No description available."}
					</p>
				</div>

				<div id="property-details" class="mb-8">
					<h2 class="mb-4 text-2xl font-bold">Property Details</h2>
					<div class="grid gap-4 sm:grid-cols-2">
						<div class="border-border bg-card rounded border p-4">
							<div class="text-muted-foreground text-sm font-medium">
								Property Type
							</div>
							<div class="mt-1 capitalize">{listing.propertySubType || "N/A"}</div>
						</div>
						<div class="border-border bg-card rounded border p-4">
							<div class="text-muted-foreground text-sm font-medium">Year Built</div>
							<div class="mt-1">{listing.yearBuilt || "N/A"}</div>
						</div>
						<div class="border-border bg-card rounded border p-4">
							<div class="text-muted-foreground text-sm font-medium">Lot Size</div>
							<div class="mt-1">{listing.lotSize || "N/A"}</div>
						</div>
						<div class="border-border bg-card rounded border p-4">
							<div class="text-muted-foreground text-sm font-medium">Garage</div>
							<div class="mt-1">
								{listing.parkingSpaces || "N/A"} Parking Spaces
							</div>
						</div>
					</div>
				</div>

				{#if listing.virtualTourUrl}
					<MatterportTour src={listing.virtualTourUrl} />
				{/if}

				{#key listing.slug}
					<MortgageCalculator price={listing?.currentPrice || 0} />
				{/key}
			</div>

			<div class="space-y-6 lg:sticky lg:top-6">
				<div class="border-border bg-card rounded border p-6">
					<div class="space-y-3">
						<div class="space-y-1">
							<h2 class="text-2xl font-bold">Get in Touch</h2>
							<p class="text-muted-foreground">
								Have questions or ready to see it in person, reach out and we'll be
								in touch shortly!
							</p>
						</div>

						{#each salesTeam as teamMember (teamMember.workEmail)}
							<div
								class="border-border flex flex-row gap-4 rounded border bg-white p-3 shadow"
							>
								<div
									class="my-auto aspect-square w-1/3 overflow-hidden rounded-full border-2 border-white"
								>
									<img
										src={teamMember.headshot}
										alt={teamMember.displayName}
										class="aspect-square object-cover"
										width="120"
										height="120"
										loading="lazy"
									/>
								</div>
								<div
									class="border-border flex w-full flex-col justify-center border-l pl-4"
								>
									<div class="text-foreground text-xl leading-6 font-semibold">
										{teamMember.displayName}
									</div>
									<div class="text-muted-foreground">{teamMember.title}</div>
									<div
										class="border-muted-foreground/10 mt-2 flex flex-row gap-4 border-t pt-2"
									>
										<a
											href={`tel:${teamMember.workNumber}`}
											class="text-primary hover:text-primary/80"
										>
											<PhoneCallIcon class="h-4 w-4" />
										</a>
										<a
											href={`mailto:${teamMember.workEmail}`}
											class="text-primary hover:text-primary/80"
										>
											<MailIcon class="h-4 w-4" />
										</a>
									</div>
								</div>
							</div>
						{/each}

						<div id="contact-form">
							{#key listing.slug}
								<SalesSlugContactForm {listing} />
							{/key}
						</div>
					</div>
				</div>

				<div class="border-border bg-card rounded border p-6">
					<div class="space-y-3">
						<div class="space-y-1">
							<h2 class="text-2xl font-bold">Get Qualified</h2>
							<p class="text-muted-foreground">
								Apply online with our preferred lender: The Green Team!
							</p>
						</div>
						<div class="flex justify-center">
							<img
								src={cloudinaryUrl("www/fergmar-com/tracigreen", {
									width: 400,
									height: 400,
									crop: "fill"
								})}
								alt="traci green"
								class="my-6 w-2/3 rounded"
								width="400"
								height="400"
								loading="lazy"
							/>
						</div>

						<div class="flex flex-col gap-2">
							<h3 class="text-lg font-bold">Traci Green</h3>
							<p class="text-muted-foreground">Originating Branch Manager</p>
							<p class="text-muted-foreground">NMLS #270407</p>
							<div class="flex flex-row items-center gap-2">
								<PhoneCallIcon class="h-4 w-4 text-[#0a8449]" />
								<p class="text-muted-foreground">(361)-676-0839</p>
							</div>
							<div class="flex flex-row items-center gap-2">
								<MailIcon class="h-4 w-4 text-[#0a8449]" />
								<p class="text-muted-foreground">traci@teamtraci.com</p>
							</div>
						</div>

						<Button
							class="w-full bg-[#0a8449]"
							href="https://tracigreen.floify.com/"
							target="_blank">Apply Now</Button
						>
					</div>
				</div>
			</div>
		</div>
	</div>
</div>

<div
	class="border-primary bg-card fixed bottom-0 left-0 z-50 w-full border-t-2 p-4 shadow-lg sm:hidden"
>
	<div class="flex items-center justify-between">
		<div>
			<div class="text-foreground font-bold">
				{#if listing.currentPrice}
					{formatCurrencyRound(listing.currentPrice)}
				{:else}
					Contact for Price
				{/if}
			</div>
			<div class="text-muted-foreground text-xs">
				{listing.bedrooms || 0} bed | {listing.totalBathrooms || 0} bath
			</div>
		</div>
		<Drawer.Root bind:open={applyDrawerOpen}>
			<Drawer.Trigger>
				<div
					class="text-primary-foreground hover:bg-primary/90 rounded bg-[#0a8449] px-4 py-2 text-sm font-medium"
				>
					Get Qualified
				</div>
			</Drawer.Trigger>
			<Drawer.Content class="flex max-h-[90vh] flex-col overflow-y-auto">
				<Drawer.Header class="text-left">
					<Drawer.Title>Get Qualified</Drawer.Title>
					<Drawer.Description
						>Apply online with our preferred lender: The Green Team!
					</Drawer.Description>
					<div class="flex justify-center">
						<img
							src={cloudinaryUrl("www/fergmar-com/tracigreen", {
								width: 400,
								height: 400,
								crop: "fill"
							})}
							alt="traci green"
							class="my-6 w-2/3 rounded"
							width="400"
							height="400"
							loading="lazy"
						/>
					</div>
					<div class="flex flex-col gap-2 text-center">
						<h3 class="text-lg font-bold">Traci Green</h3>
						<p class="text-muted-foreground">Originating Branch Manager</p>
						<p class="text-muted-foreground">NMLS #270407</p>
						<div class="flex flex-row items-center justify-center gap-2">
							<PhoneCallIcon class="h-4 w-4 text-[#0a8449]" />
							<p class="text-muted-foreground">(361)-676-0839</p>
						</div>
						<div class="flex flex-row items-center justify-center gap-2">
							<MailIcon class="h-4 w-4 text-[#0a8449]" />
							<p class="text-muted-foreground">traci@tracigreenteam.com</p>
						</div>
					</div>
				</Drawer.Header>
				<div class="px-6">
					<Button
						class="w-full bg-[#0a8449] text-base font-medium"
						href="https://apmobile.apmortgage.com/homehub/signup/traci@tracigreenteam.com"
						target="_blank">Visit Site</Button
					>
				</div>
				<Drawer.Footer class="w-full px-6 pb-6">
					<Drawer.Close>
						<Button class="w-full" variant="outline">Close</Button>
					</Drawer.Close>
				</Drawer.Footer>
			</Drawer.Content>
		</Drawer.Root>
	</div>
</div>
